import { useMemo, useRef } from "react"

import { useNavigate } from "react-router-dom"

import { useUserDetails } from "api/useUserDetails"
import { Currency, OrderDetails } from "common/contracts"

import DataTable from "components/DataTableComponent/DataTable"

import { FREE_ORDER_TABLE_COLUMNS, ORDER_TABLE_COLUMNS, ORDER_TABLE_ROWS_PER_PAGE } from "../constant"
import { createOrderTableRow } from "../utils"

interface OrderListProps {
    orders: OrderDetails[]
    currencyCodeToCurrencyMap: Map<string, Currency>
    isLoading: boolean
    isFree?: boolean
    stickyHeader?: boolean
    infiniteScrollConfig?: {
        enabled: boolean
        onLoadMore: () => void
        hasMore: boolean
        isLoadingMore?: boolean
    }
}

export const OrderList = ({ orders, currencyCodeToCurrencyMap, isLoading, isFree, stickyHeader, infiniteScrollConfig }: OrderListProps) => {
    const navigate = useNavigate();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { userDetails } = useUserDetails();

    const tableData = useMemo(() => {
        return orders.map(order => createOrderTableRow(order, currencyCodeToCurrencyMap, userDetails))
    }, [orders, currencyCodeToCurrencyMap, userDetails])

    const columns = useMemo(() => {
        return isFree ? FREE_ORDER_TABLE_COLUMNS : ORDER_TABLE_COLUMNS
    }, [isFree])

    const rowsPerPage = useMemo(() => {
        const rows = infiniteScrollConfig?.enabled
            ? orders.length || ORDER_TABLE_ROWS_PER_PAGE
            : ORDER_TABLE_ROWS_PER_PAGE
        return rows
    }, [infiniteScrollConfig?.enabled, orders.length])

    return (
        <div className="overflow-hidden flex flex-col max-h-[calc(100vh-200px)] min-h-[500px]">
            <div ref={scrollContainerRef} className="overflow-y-auto flex-1">
                <DataTable
                    key={`orders-${orders.length}-${isFree}`}
                    enableColumnResizing
                    stickyHeader={stickyHeader}
                    data={tableData}
                    columns={columns}
                    rowIdIdentifier="id"
                    onRowClick={(row) => {
                        navigate(`/orders/${row.id}?publisherId=${row.publisherId}`)
                    }}
                    isLoading={isLoading}
                    showPagination={false}
                    rowsPerPage={rowsPerPage}
                    className="[&_td]:!min-w-0 [&_th]:!min-w-0 [&_td:last-child]:!w-[12%] [&_td:last-child]:!max-w-none [&_th:last-child]:!w-[12%] [&_th:last-child]:!max-w-none"
                    infiniteScrollConfig={infiniteScrollConfig ? {
                        ...infiniteScrollConfig,
                        scrollContainerRef
                    } : undefined}
                />
            </div>
        </div>
    )
}